/**
 * Lenis-smooth page transitions:
 * - Soft routes (services/portfolio/industries/blog/about/contact): PJAX swap
 * - Hub routes (home/works/insights): curtain wipe + navigation
 * Fetch starts in parallel with leave animation for snappier feel.
 */
import Lenis from '@studio-freight/lenis';
import './tc-page-transitions.css';
import { mountSeoPage } from './seo-page-mount';

const LENIS_EASE = (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t));
const SOFT_PATH = /^\/(services|portfolio|industries|blog|about|contact)(\/|$)/;
const CURTAIN_KEY = 'tc-curtain-nav';
const META_SELECTOR =
  'meta[name="description"], meta[name="robots"], link[rel="canonical"], meta[property^="og:"], meta[name^="twitter:"]';

let lenis: Lenis | null = null;
let navigating = false;
let overlayEl: HTMLElement | null = null;
const documentCache = new Map<string, Document>();

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isSoftPath(pathname: string) {
  return SOFT_PATH.test(pathname);
}

function cacheKey(url: string) {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname + parsed.search;
  } catch {
    return url;
  }
}

function getOverlay() {
  if (!overlayEl) {
    overlayEl = document.getElementById('tc-transition-overlay');
    if (!overlayEl) {
      overlayEl = document.createElement('div');
      overlayEl.id = 'tc-transition-overlay';
      overlayEl.setAttribute('aria-hidden', 'true');
      document.body.prepend(overlayEl);
    }
  }
  return overlayEl;
}

function wait(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function waitForTransition(el: HTMLElement, fallbackMs = 900) {
  if (prefersReducedMotion()) return Promise.resolve();
  return new Promise<void>((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      el.removeEventListener('transitionend', onEnd);
      resolve();
    };
    const onEnd = (event: TransitionEvent) => {
      if (event.target === el) done();
    };
    el.addEventListener('transitionend', onEnd);
    window.setTimeout(done, fallbackMs);
  });
}

function destroyLenis() {
  if (!lenis) return;
  try {
    lenis.destroy();
  } catch {
    // ignore
  }
  lenis = null;
}

function initLenis() {
  if (!document.getElementById('tc-page') || !isSoftPath(window.location.pathname)) {
    destroyLenis();
    return;
  }

  if (lenis) return;

  lenis = new Lenis({
    duration: 1.15,
    easing: LENIS_EASE,
    orientation: 'vertical',
    smoothWheel: true,
    wheelMultiplier: 0.85,
    smoothTouch: false,
  });

  const raf = (time: number) => {
    lenis?.raf(time);
    requestAnimationFrame(raf);
  };
  requestAnimationFrame(raf);
}

function scrollToTop(smooth: boolean) {
  if (lenis && smooth && !prefersReducedMotion()) {
    lenis.scrollTo(0, { duration: 0.9, easing: LENIS_EASE });
    return;
  }
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
}

function isSameOriginNavLink(anchor: HTMLAnchorElement): URL | null {
  if (anchor.target === '_blank' || anchor.hasAttribute('download')) return null;

  const href = anchor.getAttribute('href');
  if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return null;
  }

  if (url.origin !== window.location.origin) return null;
  if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) {
    return null;
  }

  return url;
}

function syncHead(fromDoc: Document) {
  document.title = fromDoc.title;

  document.querySelectorAll(META_SELECTOR).forEach((node) => node.remove());
  fromDoc.querySelectorAll(META_SELECTOR).forEach((node) => {
    document.head.appendChild(document.importNode(node, true));
  });

  const nextLd = fromDoc.querySelector('script[type="application/ld+json"]');
  const currentLd = document.querySelector('script[type="application/ld+json"]');
  if (nextLd && currentLd) {
    currentLd.textContent = nextLd.textContent;
  } else if (nextLd && !currentLd) {
    document.head.appendChild(document.importNode(nextLd, true));
  } else if (!nextLd && currentLd) {
    currentLd.remove();
  }
}

async function fetchDocument(url: string) {
  const key = cacheKey(url);
  const cached = documentCache.get(key);
  if (cached) return cached;

  const response = await fetch(url, {
    headers: { Accept: 'text/html', 'X-TC-Pjax': '1' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch ${url}`);
  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  documentCache.set(key, doc);
  return doc;
}

async function animatePageLeave(page: HTMLElement) {
  page.classList.remove('tc-page-enter', 'tc-page-enter-active');
  page.classList.add('tc-page-leave');
  await waitForTransition(page, 700);
  page.classList.remove('tc-page-leave');
}

async function animatePageEnter(page: HTMLElement) {
  if (prefersReducedMotion()) return;
  page.classList.add('tc-page-enter');
  void page.offsetHeight;
  page.classList.add('tc-page-enter-active');
  await waitForTransition(page, 950);
  page.classList.remove('tc-page-enter', 'tc-page-enter-active');
}

async function showCurtain() {
  const overlay = getOverlay();
  overlay.classList.remove('is-leaving');
  overlay.classList.add('is-visible');
  document.documentElement.classList.add('tc-transition-active');
  await waitForTransition(overlay, 850);
}

async function hideCurtain() {
  const overlay = getOverlay();
  overlay.classList.add('is-leaving');
  overlay.classList.remove('is-visible');
  await waitForTransition(overlay, 700);
  overlay.classList.remove('is-leaving');
  document.documentElement.classList.remove('tc-transition-active');
}

async function navigateWithCurtain(url: URL) {
  const page = document.getElementById('tc-page');
  if (page && !prefersReducedMotion()) {
    document.documentElement.classList.add('tc-transition-active');
    // Soft fade current content, then wipe.
    await Promise.all([animatePageLeave(page), wait(120)]);
  }

  sessionStorage.setItem(CURTAIN_KEY, '1');
  await showCurtain();
  window.location.assign(url.href);
}

async function swapSoftPage(url: URL, pushHistory: boolean) {
  const page = document.getElementById('tc-page');
  if (!page) {
    await navigateWithCurtain(url);
    return;
  }

  document.documentElement.classList.add('tc-transition-active');

  // Start network work immediately while the leave animation plays.
  const fetchPromise = fetchDocument(url.href);
  await animatePageLeave(page);

  const doc = await fetchPromise;
  const nextPage = doc.getElementById('tc-page');
  if (!nextPage) {
    document.documentElement.classList.remove('tc-transition-active');
    await navigateWithCurtain(url);
    return;
  }

  destroyLenis();
  page.replaceWith(document.importNode(nextPage, true));
  const freshPage = document.getElementById('tc-page');
  if (!freshPage) {
    document.documentElement.classList.remove('tc-transition-active');
    window.location.assign(url.href);
    return;
  }

  document.body.className = doc.body.className;
  syncHead(doc);
  mountSeoPage({ remountNav: true });
  initLenis();
  scrollToTop(false);

  if (pushHistory) {
    history.pushState({ tcPjax: true }, '', url.pathname + url.search + url.hash);
  }

  await animatePageEnter(freshPage);
  document.documentElement.classList.remove('tc-transition-active');
}

async function navigate(url: URL) {
  if (navigating) return;
  if (
    url.pathname === window.location.pathname &&
    url.search === window.location.search &&
    !url.hash
  ) {
    return;
  }

  navigating = true;

  try {
    const currentSoft = isSoftPath(window.location.pathname);
    const targetSoft = isSoftPath(url.pathname);

    if (prefersReducedMotion()) {
      window.location.assign(url.href);
      return;
    }

    if (currentSoft && targetSoft) {
      await swapSoftPage(url, true);
    } else {
      await navigateWithCurtain(url);
    }
  } catch {
    window.location.assign(url.href);
  } finally {
    navigating = false;
  }
}

function onLinkClick(event: MouseEvent) {
  if (event.defaultPrevented) return;
  if (event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const anchor = (event.target as Element | null)?.closest?.('a[href]');
  if (!(anchor instanceof HTMLAnchorElement)) return;

  const url = isSameOriginNavLink(anchor);
  if (!url) return;

  event.preventDefault();
  void navigate(url);
}

async function onPopState() {
  if (!isSoftPath(window.location.pathname)) {
    window.location.reload();
    return;
  }

  try {
    navigating = true;
    await swapSoftPage(new URL(window.location.href), false);
  } catch {
    window.location.reload();
  } finally {
    navigating = false;
  }
}

const prefetched = new Set<string>();

function prefetchPath(pathname: string) {
  const key = cacheKey(pathname);
  if (prefetched.has(key)) return;
  prefetched.add(key);

  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = pathname;
  link.as = 'document';
  document.head.appendChild(link);

  // Warm the DOM cache for soft routes so click feels instant.
  if (isSoftPath(pathname)) {
    void fetchDocument(pathname).catch(() => {
      prefetched.delete(key);
    });
  }
}

function onPointerOver(event: Event) {
  const anchor = (event.target as Element | null)?.closest?.('a[href]');
  if (!(anchor instanceof HTMLAnchorElement)) return;
  const url = isSameOriginNavLink(anchor);
  if (!url) return;
  prefetchPath(url.pathname + url.search);
}

async function boot() {
  const fromCurtain = sessionStorage.getItem(CURTAIN_KEY) === '1';
  if (fromCurtain) {
    sessionStorage.removeItem(CURTAIN_KEY);
    const overlay = getOverlay();
    overlay.classList.add('is-visible');
    document.documentElement.classList.add('tc-transition-active');
    await wait(48);
    await hideCurtain();

    const page = document.getElementById('tc-page');
    if (page && !prefersReducedMotion()) {
      await animatePageEnter(page);
    }
  }

  initLenis();

  document.addEventListener('click', onLinkClick);
  document.addEventListener('mouseover', onPointerOver, { passive: true });
  document.addEventListener('focusin', onPointerOver, { passive: true });
  window.addEventListener('popstate', () => {
    void onPopState();
  });
}

void boot();
